import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import LoadingContext from "../../context/Loading/Loading";
import UserContext from "../../context/User/User";
import AuthService from "../../utilities/services/auth.service";

import { ORGANIZATION_PERMISSION_SLUGS } from "@blocktickets/shared-utils";
import { checkPermission } from "../../utilities/helpers";

import { getPromoStats } from "../../utilities/api";

import { PageLoadingContainer } from "../PageLoadingContainer";
import { NoPermissionsContainer } from "../NoPermissionsContainer";

import { TrackingLinks } from "./TrackingLinks";
import { TrackingLinkWrapper } from "./TrackingLinkWrapper";

export default function TrackingLinksWrapper({ eventId }) {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext);

  const { orgPermissions } = useContext(UserContext);

  const navigate = useNavigate();
  const location = useLocation();

  const orgPermissionsReady = orgPermissions?.length > 0;
  const { getPermissions } = AuthService;
  const perms = getPermissions();

  const hasPermission =
    orgPermissionsReady &&
    checkPermission(perms, ORGANIZATION_PERMISSION_SLUGS.MANAGE_TRACKING_LINKS);

  const [promos, setPromos] = useState([]);

  const [isRemoving, setIsRemoving] = useState(false);

  // flag to check whether changes to tracking link has been made or new campaign has been created to check whether to fetch data again
  // to tell if cancel button was clicked
  const [isChangesMade, setIsChangesMade] = useState(false);

  const getTrackingLinks = () => {
    showLoading();
    getPromoStats(eventId)
      .then((res) => {
        setPromos(res.data);
        hideLoading();
      })
      .catch((err) => {
        console.error(err);
        hideLoading();
      });
  };

  // Initial load and when event or permission gates change — not when the route
  // toggles between list and /add (that refetch is handled only via isChangesMade).
  useEffect(() => {
    if (!orgPermissionsReady) return;
    if (!hasPermission) {
      hideLoading();
      return;
    }
    showLoading();

    getTrackingLinks();
  }, [orgPermissionsReady, hasPermission, eventId]);

  useEffect(() => {
    if (location.pathname.includes("add")) return;
    if (!orgPermissionsReady || !hasPermission) return;
    if (!isChangesMade) return;

    getTrackingLinks();
    setIsChangesMade(false);
  }, [location, isChangesMade, orgPermissionsReady, hasPermission]);

  // handle different views
  const handleClick = () => navigate("add");

  const removeTrackingLink = () => {
    return new Promise((resolve, reject) => {
      setIsRemoving(true);
      setIsRemoving(false);
      resolve();
    });
  };

  const isAddRoute = location.pathname.includes("/add");

  if (!hasPermission) {
    return <NoPermissionsContainer />;
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer />;
  }

  if (isLoading) {
    return <PageLoadingContainer />;
  }

  return (
    <section
      className="max-width-wrapper"
      data-testid="tracking-links-wrapper-section"
    >
      {!isAddRoute ? (
        <section>
          <header className="section-header">
            <div className="section-header">
              <div className="section-heading">
                <h1>Tracking links</h1>
              </div>
              <p className="section-header-desc">
                Use custom links to track the success of your promotional
                emails, promoter partners, and more
              </p>
            </div>
          </header>
          <TrackingLinks
            promos={promos}
            removeLink={removeTrackingLink}
            isRemoving={isRemoving}
            handleClick={handleClick}
          />
        </section>
      ) : (
        <TrackingLinkWrapper
          eventId={eventId}
          setIsChangesMade={setIsChangesMade}
        />
      )}
    </section>
  );
}
